/**
 * IndexedDB wrapper for WiseEntry.
 *
 * Stores:
 *   handles  - FileSystemDirectoryHandle for the app-config folder
 *   rules    - vendor rules (id, vendorName, glCode, propertyCode, note, createdAt)
 *   batch    - in-progress batch (extracted JSON, edits, statuses) keyed by folder identity
 *   settings - app settings (jsonified)
 *
 * Note: localStorage holds the encrypted-API-key blob and the small string flags
 * that need to be available synchronously at boot. Everything bigger is here.
 */

const DB_NAME = 'wiseentry';
/** Bump when object stores or indexes change. Never lower than a version any user may already have. */
const DB_VERSION = 3;

const STORE_HANDLES  = 'handles';
const STORE_RULES    = 'rules';
const STORE_BATCH    = 'batch';
const STORE_SETTINGS = 'settings';

const APP_CONFIG_KEY = 'appConfig';

let dbPromise = null;

/**
 * Before opening at DB_VERSION, snapshot raw rules (+ metadata) to app-config/backups/
 * when IndexedDB is behind the code version (Spec §09a). Best-effort; skips if no handle
 * or permission denied.
 */
export async function maybeWritePreMigrationSnapshot() {
  let db;
  try {
    db = await new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
    });
  } catch {
    return;
  }
  try {
    if (db.version >= DB_VERSION) return;

    const { rules, handle } = await readRulesAndAppConfigHandle(db);
    if (!handle && (!rules || rules.length === 0)) return;

    const payload = JSON.stringify({
      marker: 'wiseentry-pre-migration',
      fromIdbVersion: db.version,
      targetCodeDbVersion: DB_VERSION,
      exportedAt: new Date().toISOString(),
      rules: rules || []
    }, null, 2);

    if (handle) await tryWriteJsonToAppConfigBackups(handle, payload);
  } finally {
    db.close();
  }
}

function readRulesAndAppConfigHandle(db) {
  return new Promise((resolve, reject) => {
    const rulesStoreExists = db.objectStoreNames.contains(STORE_RULES);
    const handlesStoreExists = db.objectStoreNames.contains(STORE_HANDLES);
    if (!rulesStoreExists && !handlesStoreExists) {
      resolve({ rules: [], handle: null });
      return;
    }
    const names = [];
    if (rulesStoreExists) names.push(STORE_RULES);
    if (handlesStoreExists) names.push(STORE_HANDLES);
    const tx = db.transaction(names, 'readonly');
    let rules = [];
    let handle = null;
    if (rulesStoreExists) {
      const r = tx.objectStore(STORE_RULES).getAll();
      r.onsuccess = () => { rules = r.result || []; };
      r.onerror = () => reject(r.error);
    }
    if (handlesStoreExists) {
      const h = tx.objectStore(STORE_HANDLES).get(APP_CONFIG_KEY);
      h.onsuccess = () => { handle = h.result || null; };
      h.onerror = () => reject(h.error);
    }
    tx.oncomplete = () => resolve({ rules, handle });
    tx.onerror = () => reject(tx.error);
  });
}

async function tryWriteJsonToAppConfigBackups(rootHandle, json) {
  try {
    if ((await rootHandle.queryPermission({ mode: 'readwrite' })) !== 'granted') {
      if ((await rootHandle.requestPermission({ mode: 'readwrite' })) !== 'granted') return;
    }
    const dir = await rootHandle.getDirectoryHandle('backups', { create: true });
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fh = await dir.getFileHandle(`pre-migration-rules-${stamp}.json`, { create: true });
    const w = await fh.createWritable();
    await w.write(json);
    await w.close();
  } catch (err) {
    console.warn('Pre-migration rules file snapshot skipped:', err);
  }
}

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_HANDLES))  db.createObjectStore(STORE_HANDLES);
      if (!db.objectStoreNames.contains(STORE_RULES))    db.createObjectStore(STORE_RULES, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(STORE_BATCH))    db.createObjectStore(STORE_BATCH);
      if (!db.objectStoreNames.contains(STORE_SETTINGS)) db.createObjectStore(STORE_SETTINGS);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

/**
 * Wipes IndexedDB (even when open() fails, e.g. VersionError) and localStorage keys we own.
 */
export async function resetAllLocalData() {
  dbPromise = null;
  await new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () => {
      console.warn('IndexedDB deleteDatabase blocked; close other tabs with this app.');
    };
  });
  for (const k of Object.keys(localStorage)) {
    if (k.startsWith('wiseentry.')) localStorage.removeItem(k);
  }
}

/** @returns {Promise<{ ok: true } | { ok: false, error: Error }>} */
export async function tryOpenStorage() {
  try {
    await maybeWritePreMigrationSnapshot();
    await openDb();
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * Runs a transaction; resolves only after commit and after `fn`'s Promise (if any) settles.
 * Avoid returning a bare Promise inside `fn` without tying it to completion — the txn must
 * see the queued requests before resolving the outer promise.
 */
function tx(storeName, mode, fn) {
  return openDb().then(db => new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    let fnResult;
    try {
      fnResult = fn(store);
    } catch (err) {
      reject(err);
      return;
    }

    t.oncomplete = () => {
      Promise.resolve(fnResult).then(resolve, reject);
    };
    t.onerror = () => reject(t.error || new Error('IndexedDB transaction error'));
    t.onabort = () => reject(t.error || new Error('IndexedDB transaction aborted'));
  }));
}

/* -------- Handles (FileSystemDirectoryHandle) -------- */

export async function saveAppConfigHandle(handle) {
  return tx(STORE_HANDLES, 'readwrite', s => s.put(handle, APP_CONFIG_KEY));
}

export async function loadAppConfigHandle() {
  return tx(STORE_HANDLES, 'readonly', s => new Promise((resolve, reject) => {
    const r = s.get(APP_CONFIG_KEY);
    r.onsuccess = () => resolve(r.result || null);
    r.onerror   = () => reject(r.error);
  }));
}

export async function clearAppConfigHandle() {
  return tx(STORE_HANDLES, 'readwrite', s => s.delete(APP_CONFIG_KEY));
}

export async function verifyPermission(handle, mode = 'read') {
  if (!handle) return false;
  const opts = { mode };
  if ((await handle.queryPermission(opts))   === 'granted') return true;
  if ((await handle.requestPermission(opts)) === 'granted') return true;
  return false;
}

/* -------- Rules -------- */

export async function getAllRules() {
  return tx(STORE_RULES, 'readonly', s => new Promise((resolve, reject) => {
    const r = s.getAll();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror   = () => reject(r.error);
  }));
}

export async function putRule(rule) {
  return tx(STORE_RULES, 'readwrite', s => s.put(rule));
}

export async function deleteRule(id) {
  return tx(STORE_RULES, 'readwrite', s => s.delete(id));
}

export async function clearRules() {
  return tx(STORE_RULES, 'readwrite', s => s.clear());
}

/* -------- Batch (in-progress extracted invoices) -------- */

export async function saveBatch(folderKey, payload) {
  return tx(STORE_BATCH, 'readwrite', s => s.put(payload, folderKey));
}

export async function loadBatch(folderKey) {
  return tx(STORE_BATCH, 'readonly', s => new Promise((resolve, reject) => {
    const r = s.get(folderKey);
    r.onsuccess = () => resolve(r.result || null);
    r.onerror   = () => reject(r.error);
  }));
}

export async function deleteBatch(folderKey) {
  return tx(STORE_BATCH, 'readwrite', s => s.delete(folderKey));
}

export async function listBatchKeys() {
  return tx(STORE_BATCH, 'readonly', s => new Promise((resolve, reject) => {
    const r = s.getAllKeys();
    r.onsuccess = () => resolve(r.result || []);
    r.onerror   = () => reject(r.error);
  }));
}

/* -------- Settings -------- */

export async function getSettings() {
  return tx(STORE_SETTINGS, 'readonly', s => new Promise((resolve, reject) => {
    const r = s.get('settings');
    r.onsuccess = () => resolve(r.result || null);
    r.onerror   = () => reject(r.error);
  }));
}

export async function putSettings(obj) {
  await tx(STORE_SETTINGS, 'readwrite', store => new Promise((resolve, reject) => {
    const req = store.put(obj, 'settings');
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

/* -------- Storage estimate / nuke -------- */

export async function storageEstimate() {
  if (navigator.storage && navigator.storage.estimate) {
    return navigator.storage.estimate();
  }
  return null;
}

export async function clearAllData() {
  await resetAllLocalData();
}
